// frontend/src/utils/reference/pendingQuestionTracker.js
// Owns: pending-question answer detection, carry-forward, and authority revocation on target shift
import { debugLog } from '../logging/debugLog';

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ');
}

function normalizeLeadingAnswerToken(value = '') {
  return normalizeText(value).replace(/^[\s"'([{]+/, '');
}

function startsWithAnswerWord(value = '') {
  const lower = normalizeLeadingAnswerToken(value);
  return /^(yes|no|yeah|yep|nope|nah)\b/.test(lower);
}

function splitIntoSentences(text = '') {
  return String(text)
    .split(/(?<=[.?!])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function startsLikeQuestion(text = '') {
  const lower = normalizeText(text);

  if (!lower) return false;
  if (lower.endsWith('?')) return true;

  return /^(who|what|when|where|why|how|do|does|did|is|are|was|were|can|could|would|will|have|has|had)\b/.test(
    lower
  );
}

function extractQuestionText(text = '') {
  const sentences = splitIntoSentences(text);
  if (!sentences.length) return '';

  const directQuestion = sentences.find((sentence) => sentence.includes('?'));
  if (directQuestion) return directQuestion;

  const questionLikeSentence = sentences.find((sentence) => startsLikeQuestion(sentence));
  return questionLikeSentence || '';
}

function detectExpectedAnswerType(text = '') {
  const lower = normalizeText(text);

  if (
    lower.includes('how have you been') ||
    lower.includes("how's your day going") ||
    lower.includes('how is your day going') ||
    lower.includes('how has your day been') ||
    lower.includes('how has your day been treating you') ||
    lower.includes('how are you doing') ||
    lower.includes('how are you feeling') ||
    lower.includes('what’s on your heart today') ||
    lower.includes("what's on your heart today")
  ) {
    return 'status';
  }

  if (lower.includes('how do you feel') || lower.includes('how did that make you feel')) {
    return 'emotion';
  }

  if (
    lower.includes('what happened') ||
    lower.includes('can you tell me') ||
    lower.includes('what do you mean')
  ) {
    return 'detail';
  }

  if (lower.includes('what do you think') || lower.includes('does that sound right')) {
    return 'opinion';
  }

  if (
    lower.startsWith('do you remember') ||
    lower.startsWith('did i remember') ||
    lower.startsWith('is that right')
  ) {
    return 'yes_no';
  }

  return 'general';
}

function buildPendingQuestionFromReply(
  reply = '',
  frame = null,
  lastReferencedEntity = null,
  lastReferencedTopic = null,
  questionAuthority = 'optional'
) {
  if (questionAuthority === 'none') return null;

  const questionText = extractQuestionText(reply);

  if (!questionText) return null;

  return {
    text: questionText,
    expectedAnswerType: detectExpectedAnswerType(questionText),
    frameType: frame?.type || null,
    targetEntity: lastReferencedEntity || null,
    targetTopic: lastReferencedTopic || null,
    askedAt: Date.now(),
    source: 'assistant_question',
    authority: questionAuthority === 'required' ? 'required' : 'optional',
  };
}

function messageLooksLikeGreeting(message = '') {
  const lower = normalizeText(message);

  return [
    'hi',
    'hello',
    'hey',
    'hi daisy',
    'hello daisy',
    'hey daisy',
    'good morning',
    'good afternoon',
    'good evening',
  ].includes(lower);
}

function messageAsksNewQuestion(message = '') {
  const lower = normalizeText(message);

  if (!lower) return false;
  if (lower.includes('?')) return true;

  return /^(who|what|when|where|why|how|do you|did you|can you|could you|would you|will you|is|are|was|were)\b/.test(
    lower
  );
}

function looksLikeStatusAnswer(lower = '') {
  const exactAnswers = new Set([
    'good',
    'fine',
    'ok',
    'okay',
    'pretty good',
    'not bad',
    'doing okay',
    'doing ok',
    'doing well',
    'well',
    'alright',
    'all right',
    'great',
    'better',
    'rough',
    'tired',
    'stressed',
    'overwhelmed',
    'busy',
    'sad',
    'happy',
  ]);

  if (exactAnswers.has(lower)) return true;

  const statusStarters = [
    "i'm",
    'im',
    'i am',
    'been',
    "i've been",
    'ive been',
    'doing',
    'feeling',
    'pretty',
    'not bad',
    'hanging in there',
  ];

  if (statusStarters.some((phrase) => lower.startsWith(phrase))) {
    return true;
  }

  const statusWords = [
    'good',
    'fine',
    'okay',
    'ok',
    'well',
    'better',
    'tired',
    'stressed',
    'busy',
    'sad',
    'happy',
    'rough',
    'overwhelmed',
  ];

  return statusWords.some((word) => lower.includes(word));
}

function containsEmotionalDisclosure(message = '') {
  const lower = normalizeText(message);

  if (!lower) return false;

  const emotionalPhrases = [
    "i'm lonely",
    'im lonely',
    'i am lonely',
    'feeling lonely',
    'feel lonely',
    'a bit lonely',
    'sort of lonely',
    'kind of lonely',
    'i miss',
    'missing',
    "i'm sad",
    'im sad',
    'i am sad',
    "i'm hurting",
    'im hurting',
    'i am hurting',
    "i'm overwhelmed",
    'im overwhelmed',
    'i am overwhelmed',
    "i'm struggling",
    'im struggling',
    'i am struggling',
    "i'm upset",
    'im upset',
    'i am upset',
    "i'm worried",
    'im worried',
    'i am worried',
    "i'm anxious",
    'im anxious',
    'i am anxious',
    "i'm scared",
    'im scared',
    'i am scared',
  ];

  if (emotionalPhrases.some((phrase) => lower.includes(phrase))) {
    return true;
  }

  return /\b(lonely|miss|missing|sad|hurt|hurting|overwhelmed|struggling|upset|worried|anxious|scared|grief|grieving)\b/.test(
    lower
  );
}

/**
 * Structural target-shift scoring.
 * Fresh-turn target shift can revoke stale pending authority when it
 * materially changes the active object of the turn.
 */
function computeTargetShift({
  currentEntity = null,
  previousEntity = null,
  currentTopic = null,
  previousTopic = null,
  currentNamed = [],
  previousNamed = [],
  nextQuestionAuthority = 'optional',
} = {}) {
  const newKey = currentEntity?.key || null;
  const prevKey = previousEntity?.key || null;

  const newRole = currentEntity?.role || null;
  const prevRole = previousEntity?.role || null;

  const isNewTarget = Boolean(newKey && (!prevKey || newKey !== prevKey));

  const relationshipBucketChanged = Boolean(
    newRole &&
      prevRole &&
      newRole !== prevRole
  );

  const pluralityChanged =
    Boolean(Array.isArray(currentNamed) && currentNamed.length > 1) !==
    Boolean(Array.isArray(previousNamed) && previousNamed.length > 1);

  const topicChanged = Boolean(
    currentTopic &&
      previousTopic &&
      currentTopic !== previousTopic
  );

  const targetShiftScore =
    (isNewTarget ? 0.45 : 0) +
    (relationshipBucketChanged ? 0.30 : 0) +
    (pluralityChanged ? 0.15 : 0) +
    (topicChanged ? 0.20 : 0);

  return {
    isNewTarget,
    relationshipBucketChanged,
    pluralityChanged,
    topicChanged,
    targetShiftScore,
    isMaterialShift: targetShiftScore >= 0.55,
  };
}

function detectAnswerSignal(message = '', pendingQuestion = null) {
  if (!pendingQuestion) return 'none';

  const lower = normalizeText(message);
  if (!lower) return 'none';

  const wordCount = lower.split(/\s+/).filter(Boolean).length;

  const lowSignalExact = new Set([
    'yes',
    'no',
    'yeah',
    'yep',
    'nope',
    'nah',
    'maybe',
    'probably',
    'probably not',
    'i guess',
    'not really',
    'kind of',
    'sort of',
    'a little',
    'fine',
    'good',
    'ok',
    'okay',
    'well',
    'better',
    'sad',
    'happy',
    'tired',
    'busy',
    'stressed',
    'overwhelmed',
    'never mind',
    'nevermind',
    'leave it',
    'leave it there',
    'its ok',
    "it's ok",
    'its okay',
    "it's okay",
  ]);

  const clarificationSignals = [
    'i mean',
    'i meant',
    'i just meant',
    'no i mean',
    'no i meant',
    'not one',
    'more friends',
    'other friends',
    'i have more friends',
    'i have more',
    'you only know',
    'not thinking about one',
  ];

  const detailSignals = [
    'because',
    'when',
    'after',
    'before',
    'then',
    'it was',
    'there was',
    'i was',
    'we were',
    'i have been',
    "i've been",
    'ive been',
    'lately',
    'today',
    'yesterday',
    'earlier',
  ];

  if (startsWithAnswerWord(message) && wordCount <= 3) {
    return 'low';
  }

  if (clarificationSignals.some((phrase) => lower.includes(phrase))) {
    return 'substantive';
  }

  if (containsEmotionalDisclosure(lower)) {
    return 'substantive';
  }

  if (pendingQuestion.expectedAnswerType === 'yes_no') {
    if (lowSignalExact.has(lower)) return 'low';
    if (startsWithAnswerWord(message)) return wordCount >= 4 ? 'substantive' : 'low';
    if (wordCount >= 4) return 'substantive';
    return 'low';
  }

  if (pendingQuestion.expectedAnswerType === 'status') {
    if (wordCount >= 6) return 'substantive';
    if (detailSignals.some((phrase) => lower.includes(phrase))) return 'substantive';
    if (lowSignalExact.has(lower)) return 'low';
    return wordCount >= 3 ? 'substantive' : 'low';
  }

  if (pendingQuestion.expectedAnswerType === 'emotion') {
    if (wordCount >= 6) return 'substantive';
    if (detailSignals.some((phrase) => lower.includes(phrase))) return 'substantive';
    if (lowSignalExact.has(lower)) return 'low';
    return wordCount >= 3 ? 'substantive' : 'low';
  }

  if (pendingQuestion.expectedAnswerType === 'detail') {
    if (wordCount >= 5) return 'substantive';
    if (detailSignals.some((phrase) => lower.includes(phrase))) return 'substantive';
    if (lowSignalExact.has(lower)) return 'low';
    return 'low';
  }

  if (pendingQuestion.expectedAnswerType === 'opinion') {
    if (wordCount >= 5) return 'substantive';
    if (/\b(i think|i dont think|i don't think|maybe|probably|i guess)\b/.test(lower)) {
      return wordCount >= 4 ? 'substantive' : 'low';
    }
    if (lowSignalExact.has(lower)) return 'low';
    return wordCount >= 4 ? 'substantive' : 'low';
  }

  if (wordCount >= 6) return 'substantive';
  if (detailSignals.some((phrase) => lower.includes(phrase))) return 'substantive';
  if (lowSignalExact.has(lower)) return 'low';

  return wordCount >= 4 ? 'substantive' : 'low';
}

function looksLikeAnswerToPendingQuestion(message = '', pendingQuestion = null) {
  if (!pendingQuestion) return false;

  const lower = normalizeText(message);
  if (!lower) return false;
  if (messageLooksLikeGreeting(lower)) return false;

  const nonAnswerSignals = [
    'what',
    'what?',
    'huh',
    'wait',
    'hold on',
    'just checking',
  ];

  if (nonAnswerSignals.includes(lower)) {
    return false;
  }

  const closureSignals = [
    'never mind',
    'nevermind',
    'leave it',
    'leave it there',
    'its ok',
    "it's ok",
    'its okay',
    "it's okay",
  ];

  debugLog('🧪 [PendingTracker][closureCheck]', {
    rawMessage: message,
    lower,
    lowerJson: JSON.stringify(lower),
    pendingQuestionText: pendingQuestion?.text || null,
    closureSignals,
    closureMatch: closureSignals.includes(lower),
  });

  if (closureSignals.includes(lower)) {
    return true;
  }

  if (startsWithAnswerWord(message)) {
    return true;
  }

  const softAnswerPatterns = [
    'not really',
    'just',
    'i think',
    'i feel',
    'i felt',
    'i was',
    'i am',
    "i'm",
    'im',
    'its',
    "it's",
    'kind of',
    'sort of',
    'a little',
    'pretty',
    'very',
    'mostly',
  ];

  if (softAnswerPatterns.some((phrase) => lower.startsWith(phrase))) {
    return true;
  }

  if (pendingQuestion.expectedAnswerType === 'status') {
    if (looksLikeStatusAnswer(lower)) {
      return true;
    }

    if (
      /\b(how are you|how about you|and you)\b/.test(lower) &&
      (lower.includes('ok') ||
        lower.includes('okay') ||
        lower.includes('good') ||
        lower.includes('fine') ||
        lower.includes('well'))
    ) {
      return true;
    }

    return false;
  }

  if (pendingQuestion.expectedAnswerType === 'emotion') {
    const emotionWords = [
      'sad',
      'happy',
      'angry',
      'confused',
      'hurt',
      'fine',
      'good',
      'bad',
      'lonely',
      'better',
      'worse',
      'tired',
      'stressed',
      'overwhelmed',
    ];

    if (emotionWords.some((word) => lower.includes(word))) {
      return true;
    }

    return false;
  }

  if (pendingQuestion.expectedAnswerType === 'detail') {
    if (/\b(because|when|after|before|then|it was|there was|i was|we were)\b/.test(lower)) {
      return true;
    }

    if (lower.split(/\s+/).length >= 5) {
      return true;
    }

    return false;
  }

  if (pendingQuestion.expectedAnswerType === 'opinion') {
    if (
      /\b(i think|i dont think|i don't think|maybe|probably|probably not|i guess)\b/.test(lower)
    ) {
      return true;
    }

    return false;
  }

  if (pendingQuestion.expectedAnswerType === 'yes_no') {
    return startsWithAnswerWord(message);
  }

  return false;
}

function shouldAutoReplyLocally({
  detectedAnswerToPendingQuestion = false,
  answerSignal = 'none',
  pendingAuthorityRevoked = false,
} = {}) {
  return (
    detectedAnswerToPendingQuestion &&
    !pendingAuthorityRevoked &&
    answerSignal === 'low'
  );
}

function shouldCarryForwardPendingQuestion({
  inboundMessage = '',
  detectedAnswerToPendingQuestion = false,
  answerSignal = 'none',
  pendingAuthorityRevoked = false,
  closePendingQuestion = false,
} = {}) {
  const lower = normalizeText(inboundMessage);

  if (closePendingQuestion || pendingAuthorityRevoked) {
    return false;
  }

  if (!detectedAnswerToPendingQuestion) {
    return true;
  }

  if (!lower) {
    return false;
  }

  if (answerSignal === 'low') {
    return true;
  }

  if (containsEmotionalDisclosure(lower)) {
    return false;
  }

  return false;
}

function buildPendingQuestionState({
  inboundMessage = '',
  pendingQuestion = null,
  lastAssistantMessage = '',
  frame = null,
  lastReferencedEntity = null,
  lastReferencedTopic = null,

  // new structural comparison inputs
  currentEntity = null,
  previousEntity = null,
  currentTopic = null,
  previousTopic = null,
  currentNamed = [],
  previousNamed = [],
  nextQuestionAuthority = 'optional',
} = {}) {
  const detectedAnswer = looksLikeAnswerToPendingQuestion(inboundMessage, pendingQuestion);
  const answerSignal = detectedAnswer
    ? detectAnswerSignal(inboundMessage, pendingQuestion)
    : 'none';

  const effectiveCurrentEntity = currentEntity || lastReferencedEntity || null;
  const effectivePreviousEntity = previousEntity || pendingQuestion?.targetEntity || null;
  const effectiveCurrentTopic = currentTopic || lastReferencedTopic || null;
  const effectivePreviousTopic = previousTopic || pendingQuestion?.targetTopic || null;

  const shift = computeTargetShift({
    currentEntity: effectiveCurrentEntity,
    previousEntity: effectivePreviousEntity,
    currentTopic: effectiveCurrentTopic,
    previousTopic: effectivePreviousTopic,
    currentNamed,
    previousNamed,
  });

  const pendingAnswerScore =
    answerSignal === 'substantive'
      ? 0.7
      : answerSignal === 'low'
      ? 0.4
      : 0;

  const pendingAuthorityRevoked = Boolean(
    pendingQuestion &&
      shift.isMaterialShift &&
      shift.targetShiftScore >= pendingAnswerScore
  );

  const closePendingQuestion = Boolean(
    pendingQuestion &&
      (pendingAuthorityRevoked ||
        (detectedAnswer && shift.targetShiftScore >= 0.55))
  );

  const candidateNextPendingQuestion = buildPendingQuestionFromReply(
    lastAssistantMessage,
    frame,
    lastReferencedEntity,
    lastReferencedTopic,
    nextQuestionAuthority
  );

  const shouldCarryForward = shouldCarryForwardPendingQuestion({
    inboundMessage,
    detectedAnswerToPendingQuestion: detectedAnswer,
    answerSignal,
    pendingAuthorityRevoked,
    closePendingQuestion,
  });

  const nextPendingQuestion = shouldCarryForward ? candidateNextPendingQuestion : null;

  return {
    pendingQuestion,
    detectedAnswerToPendingQuestion: detectedAnswer,
    answerSignal,
    shouldAutoReplyLocally: shouldAutoReplyLocally({
      detectedAnswerToPendingQuestion: detectedAnswer,
      answerSignal,
      pendingAuthorityRevoked,
    }),
    nextPendingQuestion,

    targetShiftScore: shift.targetShiftScore,
    targetShift: {
      isNewTarget: shift.isNewTarget,
      relationshipBucketChanged: shift.relationshipBucketChanged,
      pluralityChanged: shift.pluralityChanged,
      topicChanged: shift.topicChanged,
      isMaterialShift: shift.isMaterialShift,
    },
    pendingAuthorityRevoked,
    closePendingQuestion,
  };
}

function reconcilePendingQuestionAuthority({
  pendingQuestionState = null,
  turnSignalArbitration = null,
} = {}) {
  const state = pendingQuestionState || {};
  const pendingQuestion = state.pendingQuestion || null;

  if (!pendingQuestion) return state;
  if (pendingQuestion.authority === 'required') return state;

  const dominantSignal = turnSignalArbitration?.dominantSignal || 'none';
  const continuationSignals = new Set([
    'reasoningContinuation',
    'decisionSupport',
    'objectiveContinuation',
    'topicContinuation',
    'contextualContinuation',
    'emotionalDisclosure',
  ]);

  if (!continuationSignals.has(dominantSignal)) return state;

  return {
    ...state,
    pendingAuthorityRevoked: true,
    closePendingQuestion: true,
    nextPendingQuestion: null,
    authorityRevocationReason: `continuation_signal:${dominantSignal}`,
  };
}

export { buildPendingQuestionState, reconcilePendingQuestionAuthority };